import { vEquipmentType } from '../equipment/utils.ts'
import {
  vBoolean,
  vCoerceArray,
  vDateRange,
  vDescription,
  vNumber,
  vObject,
  vObjectId,
  vOptional,
  type vOutput,
  vPicklist,
  vPipe,
  vString,
} from '../validation/index.ts'
import type { ChargeName } from './charge.ts'
import {
  modes,
  pricingMethods,
  pricingTypes,
  shipmentDirections,
} from './common.ts'
import type { Country } from './country.ts'
import type { LoadDocumentType } from './load-document.ts'

export const analyticsSearchSchema = vObject({
  bookedBy: vOptional(vCoerceArray(vString())),
  bookedDate: vOptional(vDateRange()),
  carrier: vOptional(vCoerceArray(vString())),
  createdDate: vOptional(vDateRange()),
  deliveryDate: vOptional(vDateRange()),
  destAccount: vOptional(vCoerceArray(vString())),
  destCity: vOptional(vCoerceArray(vString())),
  destCountry: vOptional(vCoerceArray(vString())),
  destState: vOptional(vCoerceArray(vString())),
  destZip: vOptional(vCoerceArray(vString())),
  direction: vOptional(vCoerceArray(vPicklist(shipmentDirections))),
  equipmentType: vOptional(vCoerceArray(vEquipmentType())),
  glCode: vOptional(vCoerceArray(vString())),
  isInterline: vOptional(vBoolean()),
  location: vOptional(vCoerceArray(vString())),
  mode: vOptional(vCoerceArray(vPicklist(modes))),
  originAccount: vOptional(vCoerceArray(vString())),
  originCity: vOptional(vCoerceArray(vString())),
  originCountry: vOptional(vCoerceArray(vString())),
  originState: vOptional(vCoerceArray(vString())),
  originZip: vOptional(vCoerceArray(vString())),
  pickupDate: vDateRange(),
  pricingMethod: vOptional(vCoerceArray(vPicklist(pricingMethods))),
  pricingType: vOptional(vCoerceArray(vPicklist(pricingTypes))),
  quotedBy: vOptional(vCoerceArray(vString())),
  tag: vOptional(vCoerceArray(vString())),
  missingTag: vOptional(vCoerceArray(vString())),
  vendors: vOptional(vCoerceArray(vObjectId())),
  isVendor: vOptional(vBoolean()),
})

export type AnalyticsSearchDTO = vOutput<typeof analyticsSearchSchema>

export const overviewReportMetrics = ['count', 'spend'] as const
export const overviewReportIntervals = [
  'daily',
  'weekly',
  'monthly',
  'yearly',
] as const
export type OverviewAnalyticsInterval = (typeof overviewReportIntervals)[number]
export const overviewReportSchema = vObject({
  ...analyticsSearchSchema.entries,
  metric: vPicklist(overviewReportMetrics),
  interval: vOptional(vPicklist(overviewReportIntervals)),
})
export type OverviewReportDto = vOutput<typeof overviewReportSchema>

export type OverviewCountsResponse = {
  totalShipments: number
  totalSpend: number
  totalProviders: number
  unrealizedSavings?: number
}

export interface OverviewAnalyticsResult {
  id: string
  parcel: number
  truckload: number
  ltl: number
  unrealizedSavings?: number
}
export interface OverviewAnalyticsResponse {
  results: OverviewAnalyticsResult[]
}

export interface ProviderAnalyticsEntry {
  _id: string
  costPerCwt: number
  costPerHandlingUnit: number
  count: number
  providerName: string
  spend: number
  handlingUnits: number
  weight: number
  transitDays: number
  carrierRejectionCount: number
  shipperRetractionCount: number
  opportunityCount?: number
  avgConfirmationTimeMinutes?: number
}
export interface ProviderAnalyticsResponse {
  providerCount: number
  providers: ProviderAnalyticsEntry[]
  realizedSavings: number
  shipmentCount: number
  totalSpend: number
  unrealizedSavings: number
}

export interface AccessorialsEntry {
  key: ChargeName
  count: number
  cost: number
}

export interface AccessorialsProviderEntry {
  providerName: string
  accessorials: AccessorialsEntry[]
}

export interface AccessorialsReportResponse {
  providers: AccessorialsProviderEntry[]
  accessorialTypes: Array<{ key: ChargeName; name: string }>
}

export interface DocAnalyticsDocEntry {
  type: LoadDocumentType
  exists: number
  notExists: number
}
export interface DocAnalyticsProviderEntry {
  carrierId: string
  carrierName: string
  docs: DocAnalyticsDocEntry[]
}
export interface DocumentsAnalyticsResponse {
  providers: DocAnalyticsProviderEntry[]
}

export interface LanesLocationEntry {
  // TODO - replace with Pick<LoadLocation> when that model is brought into this package
  city: string
  state: string
  postalCode: string
  country: Country
  lat: number
  lng: number
  type: 'origin' | 'destination'
}
export interface LaneAnalyticData {
  count: number
  spend: number
  weight: number
  handlingUnits: number
  costPerHandlingUnit: number
}
export interface CarrierLaneData extends LaneAnalyticData {
  carrierName: string
  carrierId: string
}
export interface LanesAnalyticsEntry extends LaneAnalyticData {
  destination: LanesLocationEntry
}
export interface TableLanesAnalyticsEntry
  extends LanesAnalyticsEntry,
    LaneAnalyticData {
  origin: LanesLocationEntry
  carriers: CarrierLaneData[]
}
export interface LanesAnalyticsResponse {
  mapLanes: LanesAnalyticsEntry[]
  tableLanes: TableLanesAnalyticsEntry[]
  shipmentCount: number
}

export const auditStatisticsSchema = vObject({
  carrierId: vPipe(vString(), vDescription('The carrier ID')),
  totalShipments: vPipe(
    vNumber(),
    vDescription('Total number of shipments with this carrier'),
  ),
  totalOnTheBooks: vPipe(
    vNumber(),
    vDescription(
      'Number of shipments with payments that are not currently disputed',
    ),
  ),
  numDisputed: vPipe(
    vNumber(),
    vDescription('Number of shipments that have been disputed'),
  ),
  numManualPayments: vPipe(
    vNumber(),
    vDescription(
      'Number of on-the-books shipments with manual (shipper) payments',
    ),
  ),
  numSystemPayments: vPipe(
    vNumber(),
    vDescription('Number of on-the-books shipments with system/AI payments'),
  ),
  numApiInvoice: vPipe(
    vNumber(),
    vDescription('Number of shipments where invoice came from API'),
  ),
  numDocCenterInvoice: vPipe(
    vNumber(),
    vDescription('Number of shipments where invoice came from document center'),
  ),
  numShipperUploaded: vPipe(
    vNumber(),
    vDescription('Number of shipments where invoice was uploaded by shipper'),
  ),
  numCarrierUploaded: vPipe(
    vNumber(),
    vDescription('Number of shipments where invoice was uploaded by carrier'),
  ),
  totalSavingsAmount: vPipe(
    vNumber(),
    vDescription(
      'Total savings from disputes (originalInvoice - payment) where payment was less than original invoice',
    ),
  ),
  totalQuoteAmountWhenOverpaid: vPipe(
    vNumber(),
    vDescription('Total quote amount across all overpaid shipments'),
  ),
  totalPaymentWhenOverpaid: vPipe(
    vNumber(),
    vDescription('Total payment across all overpaid shipments'),
  ),
  totalPaymentOverQuote: vPipe(
    vNumber(),
    vDescription(
      'Total amount paid over the quoted price across all overpaid shipments',
    ),
  ),
})

const auditAnalyticsEntrySchema = vObject({
  carrierName: vPipe(vString(), vDescription('The carrier name')),
  ...auditStatisticsSchema.entries,
})
export type AuditAnalyticsEntry = vOutput<typeof auditAnalyticsEntrySchema>

export interface AuditAnalyticsResponse {
  carriers: AuditAnalyticsEntry[]
  totalShipments: number
  totalDisputed: number
  totalRealizedSavings: number
}
